import { assignSeriesColors } from "./palette";
import { labelEntry, smallBase, ratioBase, parseDay, NUMERIC_TYPES } from "./formatters";

const DAY_MS = 86400000;

/**
 * Every ISO day from `from` through `to` (UTC, inclusive), ascending; empty
 * when either is not a day or `from` is after `to`.
 */
export function daysBetween(from, to) {
	const start = parseDay(from);
	const end = parseDay(to);
	if (!start || !end || start > end) return [];
	const days = [];
	for (let t = start.getTime(); t <= end.getTime(); t += DAY_MS) {
		days.push(new Date(t).toISOString().slice(0, 10));
	}
	return days;
}

/**
 * The series a TrendChart draws (ASB-4377), from a manifest dataset and the
 * rows the API returned. For a time series (`x` = "day") the API returns one
 * row per stored row and the UI sums `y` per (x, series) (design A.3, row
 * shaping 1): the block_reason COUNTS rows are per (day, reason, source,
 * rules_drop), and the chart wants pages per (day, reason).
 *
 * When the y column is a `ratio` (A.6: "Flagged records by reason, by day"), the
 * point is num ÷ den over the rows of that (x, series), never a sum of
 * percentages, and `minBase` marks the points whose denominator is under it
 * (`small`, with the note for the tooltip). `tooltipColumns` are aggregated
 * the same way per point (a ratio column num ÷ den, other numeric columns
 * summed, others the first value) so the hover can show the count behind a %
 * and a second % on another base.
 *
 * Returns `{ xs, series, yColumn, tooltipColumns }`: `xs` the distinct x
 * values sorted ascending (ISO days sort as strings), and one series per
 * distinct `series` value, in the palette's display order, each with a value
 * per x — 0 where the day had rows but none for this series (null for a ratio
 * with no base), since no row for a (day, reason) on a day that was computed
 * means no pages. A dataset with no `series` key is one series named after the
 * y column. A series whose role is `muted` carries `hidden: true` and starts
 * hidden in the chart. `label` is the series column's `labels` entry for the
 * value, or the value itself, and `help` the entry's help.
 *
 * With `range` (`{ from, to, dataFrom }`, the page's range and the panel's
 * first complete day) and `x` = "day", `xs` spans every day from `from` —
 * clamped up to `dataFrom` when that is later — through `to`, plus any day
 * the rows carry outside it, so a day the pipeline did not produce is a gap
 * on the axis instead of vanishing and closing up the days around it. Every
 * series is null on such a day, counts included: no row at all for a day is
 * a day that was not computed, not a count of zero, and null leaves the
 * stack empty (and a line broken) where a 0 would draw a bar of nothing and
 * read as a measurement. Without `range` (a story, a chart given rows
 * alone) `xs` is the days in the rows, as before.
 */
export function buildTrendSeries(dataset, rows, resolve, range = null) {
	const { x, series: seriesKey, y, columns = [] } = dataset;
	const yColumn = columns.find((c) => c.key === y) ?? { key: y, type: "int", label: y };
	const seriesColumn = seriesKey ? columns.find((c) => c.key === seriesKey) : null;
	const roles = seriesColumn?.roles ?? null;
	const single = seriesKey ? null : yColumn.label ?? y;
	const isRatio = (c) => Array.isArray(c?.ratio) && c.ratio.length >= 2;
	const ratio = isRatio(yColumn) ? yColumn.ratio : null;
	const base = ratio ? ratioBase(yColumn, columns) : null;
	const tooltipColumns = (dataset.tooltipColumns ?? [])
		.map((key) => columns.find((c) => c.key === key))
		.filter(Boolean);

	const rowDays = new Set((rows ?? []).map((r) => String(r[x])));
	const spanned =
		x === "day" && range
			? daysBetween(
					range.dataFrom && String(range.dataFrom) > String(range.from ?? "")
						? range.dataFrom
						: range.from,
					range.to
			  )
			: [];
	const xs = Array.from(new Set([...spanned, ...rowDays])).sort();
	const points = new Map();
	const seriesValues = [];
	const num = (v) => {
		const n = Number(v);
		return Number.isFinite(n) ? n : null;
	};
	for (const row of rows ?? []) {
		const s = single ?? String(row[seriesKey] ?? "");
		if (!single) seriesValues.push(s);
		const key = `${String(row[x])}\u0000${s}`;
		let p = points.get(key);
		if (!p) {
			p = { y: 0, num: 0, den: 0, extra: {}, ratios: {} };
			points.set(key, p);
		}
		if (ratio) {
			p.num += num(row[ratio[0]]) ?? 0;
			p.den += num(row[ratio[1]]) ?? 0;
		} else {
			const n = num(row[y]);
			if (n !== null) p.y += n;
		}
		for (const col of tooltipColumns) {
			const v = row[col.key];
			if (isRatio(col)) {
				const r = (p.ratios[col.key] ??= { num: 0, den: 0 });
				r.num += num(row[col.ratio[0]]) ?? 0;
				r.den += num(row[col.ratio[1]]) ?? 0;
			} else if (NUMERIC_TYPES.includes(col.type)) {
				p.extra[col.key] = (p.extra[col.key] ?? 0) + (num(v) ?? 0);
			} else if (p.extra[col.key] === undefined && v !== null && v !== undefined) {
				p.extra[col.key] = v;
			}
		}
	}

	const colors = single
		? new Map([
				[
					single,
					{
						token: "--as-chart-1",
						color: resolve ? resolve("--as-chart-1") : undefined,
						role: null,
						hidden: false,
					},
				],
		  ])
		: assignSeriesColors(seriesValues, roles, resolve);

	const pointAt = (sx, value) => {
		const p = points.get(`${sx}\u0000${value}`);
		// A day with no rows at all was not computed: null, never a zero bar.
		if (!p) return { y: ratio || !rowDays.has(sx) ? null : 0, extra: {}, small: null };
		const extra = { ...p.extra };
		for (const [key, r] of Object.entries(p.ratios)) extra[key] = r.den > 0 ? r.num / r.den : null;
		if (!ratio) return { y: p.y, extra, small: null };
		const small = base ? smallBase(yColumn, columns, p.den) : null;
		return { y: p.den > 0 ? p.num / p.den : null, extra, small, den: p.den };
	};

	const series = Array.from(colors.keys()).map((value) => {
		const entry = labelEntry(seriesColumn, value);
		const pts = xs.map((sx) => pointAt(sx, value));
		return {
			value,
			label: entry?.label ?? value,
			help: entry?.help ?? null,
			...colors.get(value),
			data: pts.map((p) => p.y),
			points: pts,
		};
	});
	return { xs, series, yColumn, tooltipColumns };
}
